import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Dataset, DocumentQueryParams, LayerDocumentsInClickRadiusRequest, QueryResult } from '@app/modules/core/store/models';
import { HttpService } from '../../../core/services/http.service';
import { Document, HistoryDocuments } from '@app/modules/core/store/models';
import { DocumentReader } from '@app/modules/core/store/models';
import { Attachment } from 'src/app/modules/core/store/models/attachment.model';
import { Comment } from 'src/app/modules/core/store/models/comment.model';
import { CommunicationActivity } from 'src/app/modules/management/store/communication-center/communications.model';
import { DocumentsToDelete } from '@app/modules/cop/store/cop';
import { ClosestAssetByLayerRequestDto } from '../../store/impacted-assets';
import { TrafficMediaResponse } from '@app/modules/cop/store/cameras/models/traffic.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class DataApiService {
  constructor(private http: HttpService) { }

  public createDocument(datasetUId: string, document: Document): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents`;

    return this.http.post(url, { documents: [document] });
  }

  public getDocument(datasetUId: string, docUid: string, cultureCode: string = null): Observable<Document> {
    let url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${docUid}`;

    if (!!cultureCode) {
      url = `${url}?cultureCode=${cultureCode}`;
    }

    return this.http.get<QueryResult<Document>>(url);
  }

  public exportDocumentsToXlsx(datasetUId: string, query: DocumentQueryParams): Observable<any> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query/xlsx`;

    return this.http.post(url, query, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public exportClosestAssetDocumentsToXlsx(request: ClosestAssetByLayerRequestDto): Observable<any> {
    const url = `${apiBaseUrl}/v1/data/datasets/query/closest_asset/xlsx`;

    return this.http.post(url, request, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public getDocumentsGeoJsonUrl(datasetUId: string, params: DocumentQueryParams): string {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query.geojson`;

    const qp = {};
    Object.keys(params).forEach(k => {
      if (params[k] === null) {
        return;
      }
      qp[k] = typeof params[k] === 'object' ? JSON.stringify(params[k]) : params[k];
    });
    return `${url}?${this.http.queryStringFromObject(qp)}`;
  }

  public getDocumentsGeoJsonGet(datasetUId: string, params: DocumentQueryParams): Observable<QueryResult<Document>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query.geojson`;

    const qp = {};
    Object.keys(params).forEach(k => {
      if (params[k] === null) {
        return;
      }
      qp[k] = typeof params[k] === 'object' ? JSON.stringify(params[k]) : params[k];
    });
    return this.http.get(url, { params: qp });
  }

  public getDocumentsGeoJsonPost(datasetUId: string, params: DocumentQueryParams): Observable<QueryResult<Document>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query.geojson`;

    return this.http.post<QueryResult<Document>>(url, params);
  }

  public getDocuments(datasetUId: string, params: DocumentQueryParams, includeCount: boolean = false): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: includeCount });
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query?${queryString}`;

    return this.http.post<QueryResult<Document>>(url, params);
  }

  public getHorizonIntelRatingsDocuments(datasetUId: string, params: DocumentQueryParams, includeCount: boolean = false): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: includeCount });
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/ratings/query?${queryString}`;

    return this.http.post<QueryResult<Document>>(url, params);
  }

  public getHorizonIntelRatingsDocument(datasetUId: string, externalId: string, cultureCode: string = null): Observable<Document> {
    let url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${externalId}/ratings`;

    if (!!cultureCode) {
      url = `${url}?cultureCode=${cultureCode}`;
    }

    return this.http.get<Document>(url);
  }

  public deleteDocument(datasetUId: string, document: Document): Observable<void> {
    const reader = new DocumentReader(document);

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${reader.uId}`;

    return this.http.delete(url);
  }

  public deleteDocuments(docsToDelete: DocumentsToDelete[]): Observable<void> {
    const url = `${apiBaseUrl}/v1/data/datasets/delete-documents`;

    return this.http.post(url, docsToDelete);
  }

  public updateDocument(datasetUId: string, document: Document): Observable<Document> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/`;

    const updateRequest = {
      documents: [document]
    };

    return this.http.put<Document>(url, updateRequest);
  }

  public updateDocuments(datasetUId: string, updateRequest: { documents: Document[] }): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/`;

    return this.http.put<Document[]>(url, updateRequest);
  }

  public getDocumentAttachments(datasetUId: string, documentUId: string): Observable<QueryResult<Attachment>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/files/${documentUId}?skip=0&take=1000&sortField=UpdatedOn&sortDirection=Ascending`;
    return this.http.get<QueryResult<Attachment>>(url);
  }

  public getComments(datasetUId: string, documentUId: string): Observable<QueryResult<Comment>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/comments?skip=0&take=1000&sortField=CreatedOn&sortDirection=Ascending`;
    return this.http.get<QueryResult<Comment>>(url);
  }

  public createComment(datasetUId: string, documentUId: string, message: string): Observable<Comment> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/comments`;
    return this.http.post<Comment>(url, {
      datasetUId,
      documentUId,
      message
    });
  }

  public updateComment(datasetUId: string, documentUId: string, commentUId: string, message: string): Observable<Comment> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/comments/${commentUId}`;
    return this.http.put<Comment>(url, {
      uId: commentUId,
      datasetUId,
      documentUId,
      message
    });
  }

  public deleteComment(datasetUId: string, documentUId: string, commentUId: string): Observable<Comment> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/comments/${commentUId}`;
    return this.http.delete(url);
  }

  public getHistoryDocuments(datasetUId: string, documentUId: string, includeGeometry: boolean = false): Observable<QueryResult<HistoryDocuments>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/history_query/${documentUId}?includeGeometry=${includeGeometry}`;

    const req = { includeCount: true };

    return this.http.post(url, req);
  }

  public getTrafficStream(
    cameraId: string,
    width: string,
    height: string,
    coding: string,
    container: string
  ): Observable<TrafficMediaResponse> {
    const url = `${apiBaseUrl}/v1/data/traffic-cameras/${cameraId}/stream?width=${width}&height=${height}&coding=${coding}&container=${container}`;
    return this.http.get(url);
  }

  public getTrafficImage(cameraId: string): Observable<TrafficMediaResponse> {
    const url = `${apiBaseUrl}/v1/data/traffic-cameras/${cameraId}/image`;
    return this.http.get(url);
  }

  public getCommunicationActivityForDocument(documentUId: string): Observable<QueryResult<CommunicationActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/communications/communicationactivity/${documentUId}`;

    return this.http.get<QueryResult<CommunicationActivity>>(url);
  }

  public getLayerDocumentsInClickRadius(request: LayerDocumentsInClickRadiusRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/data/datasets/layerDocumentsInClickRadius`;

    return this.http.post(url, request);
  }

  public getDataset(datasetUid: string, params?: any): Observable<Dataset> {
    const queryString = this.http.queryStringFromObject(params || {});

    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUid}?${queryString}`;

    return this.http.get<Dataset>(url);
  }
}
